import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import { formatDate } from '../../i18n';
import type { MasterDataEntry } from '../masterdata/api';
import { useMasterData } from '../masterdata/queries';
import { nameOf, useAssignableUsers } from '../users/queries';
import { formatDuration, parseDuration } from './duration';
import {
  useCreateTimeEntry,
  useDeleteTimeEntry,
  useTimeEntries,
  useUpdateTimeEntry,
} from './queries';
import { Select } from '../../components/Select';

/**
 * Die erfassten Zeiten eines Tickets -- F5-01, K-176.
 *
 * **Ein Feld fuer die Dauer, kein Zeitraum.** Massgeblich ist die Dauer
 * (docs/03, Abschnitt 6); Beginn und Ende sind Beleg. Wer sie erfassen will,
 * kann es -- aber niemand soll zwei Uhrzeiten eintippen muessen, um
 * "eine halbe Stunde" zu sagen.
 *
 * Die Eingabe versteht "30", "1:30" und "1,5h" -- siehe `duration.ts`. Ein
 * Feld, das nur Minuten annimmt, erzeugt bei jeder dritten Eingabe eine
 * Fehlermeldung, und danach rechnet der Erfassende im Kopf, was der Rechner
 * tun sollte.
 *
 * **Der Timer laeuft im Browser, nicht auf dem Server.** Er merkt sich den
 * Beginn und traegt beim Anhalten die gelaufene Zeit in das Dauerfeld ein --
 * mehr nicht. Ein laufender Timer auf dem Server braeuchte eine eigene
 * Tabelle und die Antwort auf Fragen, die niemand gestellt hat: Was
 * geschieht mit einem Timer, der seit drei Tagen laeuft? Wer haelt ihn an,
 * wenn der Kollege im Urlaub ist?
 *
 * Er ueberlebt das Schliessen des Browsers, weil er in `localStorage` steht.
 * Das ist hier unbedenklich: Ein Zeitstempel ist kein Geheimnis -- anders
 * als das Zugangstoken, das aus genau diesem Grund **nicht** dort liegt
 * (docs/04, Abschnitt 4).
 */
export function TimePanel({ ticketId, canWrite }: { ticketId: string; canWrite: boolean }) {
  const { t, i18n } = useTranslation();
  const entries = useTimeEntries({ ticket_id: ticketId });
  const activityTypes = useMasterData('activity_type');
  const users = useAssignableUsers();
  const create = useCreateTimeEntry();
  const remove = useDeleteTimeEntry();
  const update = useUpdateTimeEntry();

  // Eine vertippte Dauer laesst sich korrigieren, statt den Eintrag
  // wegzuwerfen und neu zu schreiben. Der Unterschied faellt im Protokoll
  // auf: Ein geaenderter Eintrag traegt seine Vorgeschichte, ein geloeschter
  // und neu angelegter sieht aus, als haette es den ersten nie gegeben.
  const [correcting, setCorrecting] = useState<string | null>(null);
  const [correction, setCorrection] = useState('');
  const [correctionBillable, setCorrectionBillable] = useState(true);

  const [duration, setDuration] = useState('');
  const [activityTypeId, setActivityTypeId] = useState('');
  const [description, setDescription] = useState('');
  // `null` heisst: noch nicht angefasst -- dann gilt die Vorgabe der
  // Taetigkeitsart und zieht mit, wenn eine andere gewaehlt wird.
  const [billable, setBillable] = useState<boolean | null>(null);
  const [runningSince, setRunningSince] = useState<number | null>(() => readTimer(ticketId));

  const minutes = parseDuration(duration);
  const billableShown = billable ?? defaultBillable(activityTypes.data, activityTypeId);
  const total = (entries.data ?? []).reduce((sum, entry) => sum + entry.duration_minutes, 0);

  return (
    <section className="card">
      <div className="row">
        <h2>{t('time.title')}</h2>
        {total > 0 ? <Badge tone="info">{formatDuration(total)}</Badge> : null}
      </div>

      {entries.isPending ? <SkeletonTable columns={7} /> : null}
      {entries.isError ? <ErrorNotice error={entries.error} /> : null}

      {entries.data?.length === 0 ? <p className="table__muted">{t('time.empty')}</p> : null}

      {entries.data && entries.data.length > 0 ? (
        <div className="table-wrapper table-wrapper--tall">
          <table className="table table--sticky-actions">
            <thead>
              <tr>
                <th scope="col">{t('time.date')}</th>
                <th scope="col">{t('time.duration')}</th>
                <th scope="col">{t('time.activityType')}</th>
                <th scope="col">{t('time.user')}</th>
                <th scope="col">{t('time.billable')}</th>
                <th scope="col">{t('time.description')}</th>
                {canWrite ? <th scope="col">{t('tickets.actions')}</th> : null}
              </tr>
            </thead>
            <tbody>
              {entries.data.map((entry) => (
                <Fragment key={entry.id}>
                  <tr>
                    <td className="table__muted">{formatDate(entry.entry_date, i18n.language)}</td>
                    <td className="table__number">{formatDuration(entry.duration_minutes)}</td>
                    <td className="table__muted">
                      {activityTypes.data?.find((item) => item.id === entry.activity_type_id)
                        ?.name ?? t('time.withoutActivityType')}
                    </td>
                    <td className="table__muted">
                      {nameOf(users.data, entry.user_id) ?? t('tickets.unassigned')}
                    </td>
                    <td>
                      {entry.billable ? (
                        <Badge tone="success">{t('masterdata.yes')}</Badge>
                      ) : (
                        <span className="table__muted">{t('masterdata.no')}</span>
                      )}
                    </td>
                    <td className="table__muted">{entry.description}</td>
                    {canWrite ? (
                      <td>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setCorrecting(entry.id);
                            setCorrection(String(entry.duration_minutes));
                            setCorrectionBillable(entry.billable);
                          }}
                        >
                          {t('masterdata.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            remove.mutate(entry.id);
                          }}
                        >
                          {t('masterdata.delete')}
                        </Button>
                      </td>
                    ) : null}
                  </tr>

                  {/* Die Korrektur steht ueber die volle Breite und nicht in
                      der Aktionsspalte. Dort war sie zweihundert Pixel breit:
                      Das Dauerfeld abgeschnitten, das Kennzeichen daneben
                      gequetscht, die Knoepfe untereinander. Eine Maske, die
                      man erst freiscrollen muss, ist keine. */}
                  {canWrite && correcting === entry.id ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="row">
                          <input
                            className="field__control"
                            aria-label={t('time.correctDuration')}
                            value={correction}
                            onChange={(event) => {
                              setCorrection(event.target.value);
                            }}
                          />
                          {/* Auch das Kennzeichen wird korrigiert und nicht nur
                              die Dauer: Wer beim Erfassen daneben gegriffen
                              hat, muesste den Eintrag sonst loeschen und neu
                              anlegen -- und damit seine Vorgeschichte
                              wegwerfen. */}
                          <label className="field__checkbox">
                            <input
                              type="checkbox"
                              checked={correctionBillable}
                              onChange={(event) => {
                                setCorrectionBillable(event.target.checked);
                              }}
                            />
                            {t('time.billable')}
                          </label>
                          <Button
                            variant="primary"
                            busy={update.isPending}
                            disabled={parseDuration(correction) === null}
                            onClick={() => {
                              const minutes = parseDuration(correction);
                              if (minutes === null) return;
                              update.mutate(
                                {
                                  id: entry.id,
                                  payload: {
                                    duration_minutes: minutes,
                                    billable: correctionBillable,
                                  },
                                },
                                {
                                  onSuccess: () => {
                                    setCorrecting(null);
                                  },
                                },
                              );
                            }}
                          >
                            {t('app.save')}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setCorrecting(null);
                            }}
                          >
                            {t('app.cancel')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {remove.isError ? <ErrorNotice error={remove.error} /> : null}
      {update.isError ? <ErrorNotice error={update.error} /> : null}

      {canWrite ? (
        <>
          <div className="field">
            <label className="field__label" htmlFor="time-duration">
              {t('time.duration')}
            </label>
            <input
              id="time-duration"
              className="field__control"
              value={duration}
              placeholder={t('time.durationPlaceholder')}
              onChange={(event) => {
                setDuration(event.target.value);
              }}
            />
            <span className="field__hint">
              {minutes === null
                ? t('time.durationHint')
                : t('time.durationUnderstood', { value: formatDuration(minutes) })}
            </span>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="time-activity-type">
              {t('time.activityType')}
            </label>
            <Select
              id="time-activity-type"
              className="field__control"
              value={activityTypeId}
              onChange={(event) => {
                setActivityTypeId(event.target.value);
              }}
            >
              <option value="">{t('time.defaultActivityType')}</option>
              {(activityTypes.data ?? [])
                .filter((item) => item.is_active !== false)
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </Select>
          </div>

          {/* Das Kennzeichen -- F5-01, K-176. Vorbelegt aus der Taetigkeitsart
              und dennoch sichtbar: Ohne das Kaestchen erfaehrt der Erfassende
              erst in der Tabelle darueber, wie sein Eintrag gezaehlt wird --
              und Taetigkeiten, die nicht abgerechnet werden, gibt es in jeder
              Woche. */}
          <div className="field">
            <label className="field__checkbox">
              <input
                id="time-billable"
                type="checkbox"
                checked={billableShown}
                onChange={(event) => {
                  setBillable(event.target.checked);
                }}
              />
              {t('time.billable')}
            </label>
            <span className="field__hint">{t('time.billableHint')}</span>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="time-description">
              {t('time.description')}
            </label>
            <input
              id="time-description"
              className="field__control"
              value={description}
              onChange={(event) => {
                setDescription(event.target.value);
              }}
            />
          </div>

          {/* Start und Stopp -- F5-01, K-176. Der Knopf traegt beim Anhalten
              die gelaufene Zeit in das Dauerfeld ein und legt nichts an:
              Massgeblich bleibt die Dauer, und wer nebenbei telefoniert hat,
              korrigiert sie, bevor er speichert. */}
          <div className="row">
            {runningSince === null ? (
              <Button
                variant="secondary"
                onClick={() => {
                  const started = Date.now();
                  setRunningSince(started);
                  writeTimer(ticketId, started);
                }}
              >
                {t('time.start')}
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  const gelaufen = Math.max(1, Math.round((Date.now() - runningSince) / 60_000));
                  setDuration(String(gelaufen));
                  setRunningSince(null);
                  writeTimer(ticketId, null);
                }}
              >
                {t('time.stop')}
              </Button>
            )}
            {runningSince !== null ? (
              <span className="field__hint" role="status">
                {t('time.running', {
                  since: new Date(runningSince).toLocaleTimeString(i18n.language),
                })}
              </span>
            ) : null}
          </div>

          {create.isError ? <ErrorNotice error={create.error} /> : null}

          <Button
            variant="primary"
            disabled={minutes === null || minutes <= 0}
            busy={create.isPending}
            onClick={() => {
              if (minutes === null) return;
              create.mutate(
                {
                  ticket_id: ticketId,
                  duration_minutes: minutes,
                  description,
                  billable: billableShown,
                  ...(activityTypeId ? { activity_type_id: activityTypeId } : {}),
                },
                {
                  onSuccess: () => {
                    setDuration('');
                    setDescription('');
                    setBillable(null);
                  },
                },
              );
            }}
          >
            {t('time.add')}
          </Button>
        </>
      ) : null}
    </section>
  );
}

/**
 * Die Vorbelegung des Kennzeichens.
 *
 * Deckungsgleich mit `_billable` im Dienst: die Vorgabe der gewaehlten
 * Taetigkeitsart, ohne Auswahl die der Vorgabeart, und ohne beides
 * "abrechenbar". Sie hier zu wiederholen ist bewusst -- das Kaestchen muss
 * zeigen, was gespeichert wuerde, und dafuer braucht es die Antwort vor dem
 * Abschicken. Massgeblich bleibt der Server: Die Oberflaeche schickt das
 * Kennzeichen ausdruecklich mit, damit nicht zweierlei gilt.
 */
function defaultBillable(types: MasterDataEntry[] | undefined, activityTypeId: string): boolean {
  const list = types ?? [];
  const chosen = activityTypeId
    ? list.find((item) => item.id === activityTypeId)
    : list.find((item) => item.is_default === true && item.is_active !== false);
  return (chosen as { billable_default?: boolean } | undefined)?.billable_default ?? true;
}

/**
 * Der laufende Timer je Ticket.
 *
 * In `localStorage` und nicht im Zustand der Komponente: Wer den Browser
 * schliesst, waehrend er beim Kunden steht, soll seine Zeit nicht verlieren.
 * Ein Zeitstempel ist kein Geheimnis -- anders als das Zugangstoken, das aus
 * genau diesem Grund **nicht** dort liegt.
 */
const TIMER_PREFIX = 'itscrm.timer.';

function readTimer(ticketId: string): number | null {
  const raw = window.localStorage.getItem(`${TIMER_PREFIX}${ticketId}`);
  if (raw === null) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

function writeTimer(ticketId: string, started: number | null): void {
  if (started === null) {
    window.localStorage.removeItem(`${TIMER_PREFIX}${ticketId}`);
    return;
  }
  window.localStorage.setItem(`${TIMER_PREFIX}${ticketId}`, String(started));
}
